import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import Cookies from 'js-cookie'
import { Modal } from 'react-bootstrap'
import { constants } from '../../../config/constants';
import { zynity_api, zynity_website } from '../../../config/external-routes';
import { loadAdminContent } from '../../../helpers/loadAdminContent';
import { Loading } from '../../ui/loading/Loading';
import { Accordion } from './accordion/Accordion';
import './body.scss';
import { Link } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { EngagementWidgetHost } from '../../engagement/EngagementWidgetHost';
import { getUserOrganizations } from '../../../store/user_organizations';
import { useNavigate } from 'react-router-dom';

export const Body = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const focusOrgId = new URLSearchParams(location.search).get('org'); // string | null
  const yii_url = zynity_website.yii_website_url;
  const user = useSelector(state => state.auth)
  const accessToken = user.access_token || Cookies.get(constants.zynity_api_access_token_cookie_name)
  const user_organizations = useSelector(state => state.user_organizations)

  const [userNewsContent, setUserNewsContent] = useState(false)
  const [showAddOrganization, setShowAddOrganization] = useState(false)
  const [organizationName, setOrganizationName] = useState('')
  const [organizationCreateError, setOrganizationCreateError] = useState('')
  const [organizationCreating, setOrganizationCreating] = useState(false)

  const closeAddOrganization = () => {
    if (organizationCreating) return
    setShowAddOrganization(false)
    setOrganizationName('')
    setOrganizationCreateError('')
  }

  const createOrganization = async (event) => {
    event.preventDefault()
    const name = organizationName.trim()
    if (!name) {
      setOrganizationCreateError('Enter an organization name.')
      return
    }

    setOrganizationCreating(true)
    setOrganizationCreateError('')
    try {
      const response = await axios.post(
        zynity_api.organizations,
        { name },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      )
      const organization = response.data?.organization
      await dispatch(getUserOrganizations())
      setShowAddOrganization(false)
      setOrganizationName('')

      if (organization?.id) {
        navigate(`/organizations/${organization.id}/profile`)
      }
    } catch (error) {
      setOrganizationCreateError(
        error.response?.data?.errors?.name?.[0]
        || error.response?.data?.error
        || 'Unable to create the organization. Please try again.'
      )
    } finally {
      setOrganizationCreating(false)
    }
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await loadAdminContent(constants.admin_contents.user_news, user.access_token);
        if (alive) setUserNewsContent(res || '');
      } catch (e) {
        if (alive) setUserNewsContent('<em>No news available.</em>');
      }
    })();
    return () => { alive = false; };
  }, [user.access_token]);

  if (user_organizations.isLoading) {
    return <Loading centerPage={true} />
  }

  const organizations = user_organizations?.data?.data || user_organizations?.data || [];

  // Current month + year for the Member News card
  const currentDate = new Date();
  const monthYear = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <>
      {/* Mobile-only quick links to jump to News / Plan */}
      <div className="d-lg-none text-end quick-links">
        <a href="#news-panel" className="qlink">News</a>
        <span className="sep">·</span>
        <a href="#plan-panel" className="qlink">Plan</a>
      </div>

      <div className="row mt-2 mt-sm-3 mt-lg-5">
        {/* CENTER on desktop, FIRST on mobile: Organizations */}
        <div className="col-12 col-lg-6 order-1 order-lg-2">
          <div className="d-flex justify-content-end mb-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              style={{ fontSize: '0.8rem', padding: '0.3rem 0.55rem' }}
              onClick={() => setShowAddOrganization(true)}
            >
              Add organization
            </button>
          </div>
          <div className="row">
            <div className="col-12 p-0">
              {organizations.length === 0 && (
                <div className="alert alert-light" role="alert">
                  No organizations found for your account.
                </div>
              )}
              {organizations.map(org => (
                <Accordion
                  key={org.id}
                  org={org}
                  user={user}
                  headerColor={org?.color_key || org?.colorKey || org?.brand_key || org?.color}
                  initialOpen={String(org?.id) === String(focusOrgId)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* LEFT on desktop, SECOND on mobile: News */}
        <div className="col-12 col-lg-3 order-2 order-lg-1">
          {(userNewsContent || userNewsContent === '') && (
            <div id="news-panel" className="usernews mt-3 mt-lg-0">
              {/* Keep the card title as content (only column titles were removed) */}
              <h5>Member News</h5>
              <p style={{ marginTop: '-10px', marginBottom: '20px', fontStyle: 'italic', fontWeight: 'bold' }}>
                {monthYear}
              </p>
              <div dangerouslySetInnerHTML={{ __html: userNewsContent }}></div>
            </div>
          )}
        </div>

        {/* RIGHT on desktop, THIRD on mobile: Development */}
        <div className="col-12 col-lg-3 order-3 order-lg-3">
          <div id="plan-panel" className="mydev-card mt-3 mt-lg-0">
            <a href={`${yii_url}/mystory/index`} >
              <button className="theme-btn btn-style-one dev-btn w-100 mb-2">My Story</button>
            </a>
            <a href={`${yii_url}/Development/index`} >
              <button className="theme-btn btn-style-one dev-btn w-100 mb-2">My Development</button>
            </a>
            <Link to="/how-lead">
              <button className="theme-btn btn-style-one dev-btn w-100">How You Lead</button>
            </Link>
          </div>
        </div>
      </div>

      <EngagementWidgetHost enabled={!!user?.engagement_engine_enabled} />

      <Modal show={showAddOrganization} onHide={closeAddOrganization} centered>
        <form onSubmit={createOrganization}>
          <Modal.Header closeButton={!organizationCreating}>
            <Modal.Title>Add organization</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <label className="form-label" htmlFor="new-organization-name">Organization name</label>
            <input
              id="new-organization-name"
              className={`form-control${organizationCreateError ? ' is-invalid' : ''}`}
              value={organizationName}
              onChange={(event) => setOrganizationName(event.target.value)}
              maxLength={255}
              autoFocus
              disabled={organizationCreating}
            />
            {organizationCreateError && (
              <div className="invalid-feedback">{organizationCreateError}</div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={closeAddOrganization} disabled={organizationCreating}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={organizationCreating}>
              {organizationCreating ? 'Creating…' : 'Create organization'}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  )
}
